/* -------------------------------------------------------------------------
 * /data: the contact form, the sample webhook's tabs, and the links that
 * scroll to them. The form checks with the same rules as the server
 * (../lead.ts) and posts JSON to lead.controller.ts.
 * ---------------------------------------------------------------------- */
import {
  checkLead,
  firstName,
  hasErrors,
  isFreeEmail,
  replyBy,
  sourceOf,
  type LeadErrors,
  type LeadField,
  type LeadSource,
} from '../lead';
import { $, $$, attr, tablist, text } from './dom';
import { reducedMotion } from './motion';

/** The sticky header and a little air: what scroll-margin-top keeps clear. */
const HEADER_CLEARANCE = 80;
/** How long a smooth scroll may take before its follow-up runs anyway. */
const SCROLL_FALLBACK_MS = 900;

/**
 * Scrolls `target` to the top of the window, under the header (its
 * scroll-margin-top), then calls `then` once the scroll has ended: on
 * `scrollend`, or a moment later where that event never comes.
 */
export function scrollThen(target: Element, then: () => void): void {
  const offBy = target.getBoundingClientRect().top - HEADER_CLEARANCE;
  if (reducedMotion() || Math.abs(offBy) < 2) {
    if (Math.abs(offBy) >= 2) target.scrollIntoView({ block: 'start' });
    then();
    return;
  }
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    window.removeEventListener('scrollend', finish);
    then();
  };
  window.addEventListener('scrollend', finish);
  window.setTimeout(finish, SCROLL_FALLBACK_MS);
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export type LeadReply =
  { ok: true } | { errors: LeadErrors } | { failed: true };

export interface LeadRequest {
  name: string;
  company: string;
  email: string;
  needs: string[];
  message: string | null;
  source: LeadSource;
  website: string;
  timeZone: string | null;
}

/** Posts the lead. Field errors come back as the server words them. */
export async function sendLead(body: LeadRequest): Promise<LeadReply> {
  try {
    const res = await fetch('/api/leads/data', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) return { ok: true };
    if (res.status === 400) {
      const reply = (await res.json().catch(() => null)) as {
        errors?: unknown;
      } | null;
      if (reply?.errors && typeof reply.errors === 'object') {
        return { errors: reply.errors };
      }
    }
    return { failed: true };
  } catch {
    return { failed: true };
  }
}

function timeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

const FIELDS: LeadField[] = ['name', 'company', 'email', 'message'];

export interface DataPageOptions {
  send?: (body: LeadRequest) => Promise<LeadReply>;
  now?: () => Date;
}

export function dataPage(root: HTMLElement, options: DataPageOptions = {}) {
  const send = options.send ?? sendLead;
  const now = options.now ?? (() => new Date());

  const card = $(root, '[data-lead]');
  const payload = $(root, '#payload');

  // The sample webhook: one panel per tab.
  const tabs = $(root, '[data-hook-tabs]');
  if (tabs) {
    const panels = $$(root, '.dp-pre[role="tabpanel"]');
    tablist(tabs, (index) => {
      panels.forEach((p, i) => (p.hidden = i !== index));
    });
  }

  if (!card) return;
  const form = $<HTMLFormElement>(card, '[data-lead-form]');
  const sent = $(card, '[data-lead-sent]');
  const submit = $<HTMLButtonElement>(card, '[data-lead-submit]');
  const fail = $(card, '[data-lead-fail]');
  const hint = $(card, '#lead-email-hint');
  if (!form || !sent || !submit || !fail || !hint) return;

  const inputs = {
    name: $<HTMLInputElement>(form, '#lead-name'),
    company: $<HTMLInputElement>(form, '#lead-company'),
    email: $<HTMLInputElement>(form, '#lead-email'),
    message: $<HTMLTextAreaElement>(form, '#lead-message'),
  };
  const honeypot = $<HTMLInputElement>(form, 'input[name="website"]');
  const chips = $$<HTMLButtonElement>(form, '[data-need]');
  const label = submit.textContent ?? '';
  // Where the visitor came from, as the page was when it loaded.
  const source = sourceOf(window.location.href, document.referrer);
  const picked = new Set<string>();
  let busy = false;

  const errorOf = (field: LeadField) => $(form, `#lead-${field}-err`);

  /** Error first, then the email's hint; the message's note always. */
  const describe = (field: LeadField) => {
    const input = inputs[field];
    if (!input) return;
    const ids: string[] = [];
    if (field === 'message') ids.push('lead-message-note');
    if (errorOf(field)?.textContent) ids.push(`lead-${field}-err`);
    if (field === 'email' && !hint.hidden) ids.push('lead-email-hint');
    if (ids.length) attr(input, 'aria-describedby', ids.join(' '));
    else input.removeAttribute('aria-describedby');
  };

  const paintHint = () => {
    const email = inputs.email?.value ?? '';
    hint.hidden = Boolean(errorOf('email')?.textContent) || !isFreeEmail(email);
    describe('email');
  };

  const setError = (field: LeadField, message: string | null) => {
    const input = inputs[field];
    text(errorOf(field), message ?? '');
    if (message) attr(input, 'aria-invalid', 'true');
    else input?.removeAttribute('aria-invalid');
    describe(field);
  };

  const showErrors = (errors: LeadErrors) => {
    for (const field of FIELDS) setError(field, errors[field] ?? null);
    paintHint();
    const first = FIELDS.find((f) => errors[f]);
    if (first) inputs[first]?.focus();
  };

  for (const field of FIELDS) {
    inputs[field]?.addEventListener('input', () => {
      if (errorOf(field)?.textContent) setError(field, null);
      if (field === 'email') paintHint();
    });
  }

  const paintChips = () => {
    for (const chip of chips) {
      attr(chip, 'aria-pressed', String(picked.has(chip.dataset.need ?? '')));
    }
  };
  for (const chip of chips) {
    chip.addEventListener('click', () => {
      const need = chip.dataset.need ?? '';
      if (picked.has(need)) picked.delete(need);
      else picked.add(need);
      paintChips();
    });
  }

  const showSent = (lead: { name: string; email: string; needs: string[] }) => {
    text($(sent, '[data-sent-first]'), firstName(lead.name));
    text($(sent, '[data-sent-email]'), lead.email);
    text($(sent, '[data-sent-by]'), replyBy(now()));
    const tokens = $(sent, '[data-sent-needs]');
    if (tokens) {
      tokens.replaceChildren(
        ...lead.needs.map((need) => {
          const li = document.createElement('li');
          li.textContent = need;
          return li;
        }),
      );
      tokens.hidden = lead.needs.length === 0;
    }
    form.hidden = true;
    sent.hidden = false;
    $(sent, '[data-sent-h]')?.focus({ preventScroll: true });
    // The card shrinks; move only if its top is now out of sight.
    const top = card.getBoundingClientRect().top;
    if (top < HEADER_CLEARANCE - 10 || top > window.innerHeight) {
      card.scrollIntoView({
        behavior: reducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (busy) return;
    const { lead, errors } = checkLead({
      name: inputs.name?.value,
      company: inputs.company?.value,
      email: inputs.email?.value,
      needs: [...picked],
      message: inputs.message?.value,
    });
    fail.hidden = true;
    showErrors(errors);
    if (hasErrors(errors)) return;

    busy = true;
    submit.disabled = true;
    attr(submit, 'aria-busy', 'true');
    text(submit, 'Sending…');
    void send({
      ...lead,
      source,
      website: honeypot?.value ?? '',
      timeZone: timeZone(),
    })
      .then((reply) => {
        if ('ok' in reply) showSent(lead);
        else if ('errors' in reply) showErrors(reply.errors);
        else fail.hidden = false;
      })
      .finally(() => {
        busy = false;
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        text(submit, label);
      });
  });

  // Back to the form: who they are stays, what they asked for goes.
  $(sent, '[data-lead-again]')?.addEventListener('click', (e) => {
    e.preventDefault();
    if (inputs.message) inputs.message.value = '';
    setError('message', null);
    picked.clear();
    paintChips();
    fail.hidden = true;
    sent.hidden = true;
    form.hidden = false;
    inputs.name?.focus();
  });

  // The header's "Talk to us": to the form, into its first field.
  for (const link of $$(document, '[data-talk-cta]')) {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      scrollThen(card, () => {
        const target = form.hidden ? $(sent, '[data-sent-h]') : inputs.name;
        target?.focus({ preventScroll: true });
      });
    });
  }

  // "See a sample webhook payload ↓": to the code, onto its chosen tab.
  $(sent, '[data-to-payload]')?.addEventListener('click', (e) => {
    if (!payload) return;
    e.preventDefault();
    scrollThen(payload, () => {
      $(payload, '[role="tab"][aria-selected="true"]')?.focus({
        preventScroll: true,
      });
    });
  });
}
